export const ROLES = {
    SUPER_ADMIN: 'super_admin',
    ADMIN: 'admin',
    MANAGER: 'manager',
    AGENT: 'agent',
    CHAT_SUPPORT: 'chat_support',
    TELECALLER: 'telecaller',
    // Dispatch desk: scans parcels out/in and prints labels, manifests and reports.
    // Deliberately NOT part of ALL_STAFF — it must not inherit order/customer access.
    DISPATCH: 'dispatch',
    // Legacy alias for the same desk, kept so an account created before the rename
    // keeps working.
    HANDOVER: 'handover',
    // Parcel QC inspector: weighs, approves/rejects and counts parcels on the QC page.
    // Like dispatch, deliberately NOT part of ALL_STAFF — no orders, customers or money.
    QC: 'qc'
};

const ALL_STAFF = [
    ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER,
    ROLES.AGENT, ROLES.CHAT_SUPPORT, ROLES.TELECALLER
];

// Who may open the handover scan + print centre. Mirrors the SERVER's
// HANDOVER_ROLES exactly (all staff roles + the two desk roles): the server was
// already letting agents and telecallers record scans, while the client's
// narrower list made the page unreachable - access could not be granted from
// the staff permission module. Per-person narrowing lives in allowedModules.
const HANDOVER_STAFF = [...ALL_STAFF, ROLES.DISPATCH, ROLES.HANDOVER];
// Printing mirrors the server's PRINT_ROLES (shipment roles + desk roles).
// Parcel QC: inspectors + managers act; the dispatch desk may watch (read-only).
const QC_VIEW = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.QC, ROLES.DISPATCH, ROLES.HANDOVER];
const HANDOVER_PRINT = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.AGENT, ROLES.DISPATCH, ROLES.HANDOVER];

export const PERMISSIONS = {
    VIEW_CHAT: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.CHAT_SUPPORT],
    MANAGE_USERS: [ROLES.SUPER_ADMIN],
    DELETE_ORDERS: [ROLES.SUPER_ADMIN],
    VIEW_ANALYTICS: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    EXPORT_DATA: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    UPLOAD_FILES: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER]
};

// UI capability map — keyed by the string keys the UI already uses
// (canViewOrders, canViewCustomers, ...). Each maps to the roles allowed.
export const CAPABILITIES = {
    canViewDashboard: ALL_STAFF,
    canViewOrders: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.AGENT, ROLES.TELECALLER],
    canEditOrders: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    canDeleteOrders: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    canViewCustomers: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.AGENT, ROLES.TELECALLER, ROLES.CHAT_SUPPORT],
    canViewChat: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.CHAT_SUPPORT, ROLES.AGENT],
    canViewAnalytics: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    canViewProducts: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    canViewParser: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.AGENT, ROLES.TELECALLER],
    canViewShipping: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    // Tracking is READ-heavy (where is this parcel?) and every team member
    // fields that question on calls - so the page is open to all staff, with
    // per-person narrowing via allowedModules. Courier DESKS stay on
    // canViewShipping.
    canViewTracking: ALL_STAFF,
    canViewFinance: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    canViewReferrals: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    // Rewards wallet. Viewing a customer's own balance rides on canViewCustomers (an
    // agent on a call needs it); the programme-wide liability view and its settings do
    // not, because activating the scheme is what creates the liability.
    canViewRewards: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    canManageRewards: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    canViewStaff: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    canManageStaff: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    canViewTodo: ALL_STAFF,
    canViewCalendar: ALL_STAFF,
    canManageSettings: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    canViewComplaints: ALL_STAFF,
    canCreateComplaints: ALL_STAFF, // any staff (incl. agent/telecaller/chat) can log a ticket
    canManageComplaints: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    // Handover desk
    canViewHandover: HANDOVER_STAFF,
    canPrintHandoverDocs: HANDOVER_PRINT,
    canReverseHandover: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    // Parcel QC (server/qc mirrors these: QC_ROLES / QC_VIEW_ROLES / MANAGER_ROLES).
    canViewQc: QC_VIEW,
    canRecordQc: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.QC],
    canManageQcSettings: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],

    canViewTelecallerWorkspace: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.TELECALLER],

    // Telesales / customer-handling capabilities
    canAddCallLog: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.AGENT, ROLES.TELECALLER],
    canAddFeedback: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.AGENT, ROLES.TELECALLER],
    canAddOrderFromProfile: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.AGENT, ROLES.TELECALLER],
    canOverrideDND: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    canDeleteCustomer: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    canExportCustomers: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],

    // Pre-Shipment Desk capabilities
    canCallCustomer: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.AGENT, ROLES.TELECALLER],
    canAddRemark: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.AGENT, ROLES.TELECALLER],
    canMarkCOD: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.AGENT, ROLES.TELECALLER],
    canCreateFollowUp: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.AGENT, ROLES.TELECALLER],
    canAssignCourier: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    canBulkMove: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    canOverrideValidation: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    canCancelOrder: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    canHoldOrder: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.AGENT],

    // Ekart courier (Phase 4). Warehouse=AGENT (shipment only), Telecaller=read+call, Support=read.
    canViewEkartAnalytics: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    canManageEkart: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.AGENT],
    canExportEkart: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    canOverrideEkart: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    // Courier escalation (Section 16)
    canViewCourierEscalations: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.AGENT, ROLES.TELECALLER, ROLES.CHAT_SUPPORT],
    // Approval Desk (30 Oct 2026): managers and above decide COD-without-₹99, repeat shipments, discounts.
    canViewApprovals: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    canManageCourierEscalations: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    canAssignCourierEscalations: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    canCloseCourierEscalations: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
    canReceiveCourierSlaAlerts: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
};

// Normalize a role string (handles 'ADMIN' / 'Admin' / 'admin').
export const normalizeRole = (role) => (role ? String(role).trim().toLowerCase() : '');

export const hasPermission = (role, permission) => {
    const r = normalizeRole(role);
    return PERMISSIONS[permission]?.map(normalizeRole).includes(r) || false;
};

// UI capability check. Unknown capability keys default to DENY (fail-closed).
/**
 * can() for a USER: the role's capabilities, plus per-person grants. Today the only
 * grant is the Telecaller Workspace page for someone whose role doesn't include it
 * (users/{uid}/tcCaps/workspace === true — the same flag the server's tcCaps honours),
 * e.g. an "agent" login who calls customers and has a daily target.
 */
export const canUser = (user, capability) => {
    if (can(user?.role, capability)) return true;
    if (capability === 'canViewTelecallerWorkspace' && user?.tcCaps?.workspace === true) return true;
    return false;
};

export const can = (role, capability) => {
    const r = normalizeRole(role);
    const allowed = CAPABILITIES[capability];
    if (!allowed) return false; // fail-closed for unknown capabilities
    return allowed.map(normalizeRole).includes(r);
};
